import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCustomerAuth } from "@/lib/customerAuth";
import { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 5.1 29.4 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 5.1 29.4 3 24 3 16 3 9.1 7.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-3.6-11.3-8.4l-6.5 5C9 40.3 15.9 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 35.7 45 30.4 45 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
);

export default function MemberAuth() {
  const { login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success("Bienvenue dans votre espace membre.");
      navigate("/membre");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/membre";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="member-auth-page" className="pt-32 pb-24 min-h-[80vh] flex items-center">
      <div className="mx-auto w-full max-w-md px-5">
        <div className="text-center mb-8">
          <span className="inline-grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-gold mx-auto"><User size={20} /></span>
          <h1 className="mt-5 font-serif text-3xl text-white">Espace Membre</h1>
          <p className="mt-2 text-sm text-white/50">Programme fidélité · adresses & historique</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel p-8">
          <div className="grid grid-cols-2 gap-2 mb-6">
            {[["login", "Connexion"], ["register", "Inscription"]].map(([k, l]) => (
              <button key={k} data-testid={`member-tab-${k}`} onClick={() => setMode(k)}
                className={`rounded-full py-2 text-sm transition-colors ${mode === k ? "bg-gold text-black" : "border border-white/15 text-white/70 hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>

          <button onClick={googleLogin} data-testid="google-login-btn" className="w-full flex items-center justify-center gap-3 rounded-full bg-white px-6 py-3 text-sm font-medium text-black hover:bg-white/90 transition-colors">
            <GoogleIcon /> Continuer avec Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-white/40">ou par email</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Prénom</Label>
                  <Input data-testid="member-firstname" value={form.first_name} onChange={set("first_name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
                <div>
                  <Label className="text-white/70">Nom</Label>
                  <Input data-testid="member-lastname" value={form.last_name} onChange={set("last_name")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-white/70">Email</Label>
              <Input data-testid="member-email" type="email" value={form.email} onChange={set("email")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
            </div>
            {mode === "register" && (
              <div>
                <Label className="text-white/70">Téléphone</Label>
                <Input data-testid="member-phone" value={form.phone} onChange={set("phone")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
              </div>
            )}
            <div>
              <Label className="text-white/70">Mot de passe</Label>
              <Input data-testid="member-password" type="password" value={form.password} onChange={set("password")} className="mt-2 bg-ink border-white/15 focus-visible:ring-gold" />
            </div>
            <button type="submit" disabled={loading} data-testid="member-submit" className="w-full rounded-full bg-gold px-6 py-3 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft disabled:opacity-60">
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
